import { Check, Clock, Copy, Share2 } from "lucide-react";
import { useState } from "react";

import logoWhiteUrl from "@/assets/svg/logo-white.svg";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";

import { BREB_KEY_TONE } from "./brebKeySources";

export interface BrebKeyPlateProps {
  keyValue: string;
  /** What the payer's bank shows as the recipient; omitted while the key is still being confirmed. */
  holderName?: string | null;
  /** Copy and share. Off for a preview of a key that does not exist yet, or one still being confirmed. */
  showActions?: boolean;
  /** A key not confirmed yet cannot receive: a badge says so and the plate drops its brand green. */
  pending?: boolean;
  /** The same greyer plate without the badge: a preview of a value that cannot be registered (yet). */
  dimmed?: boolean;
  className?: string;
}

const canShareText = (): boolean => typeof navigator !== "undefined" && typeof navigator.share === "function";

// Same green placard as the QR card: the key is a destination of the same kind, set large enough to read aloud.
export function BrebKeyPlate({
  keyValue,
  holderName,
  showActions = true,
  pending = false,
  dimmed = false,
  className,
}: BrebKeyPlateProps) {
  const { t } = useTranslations("payins");
  const { copiedValue, copy } = useCopyToClipboard(t("breb_key_copy_error"));
  const [canShare] = useState(canShareText);
  const copied = copiedValue === keyValue;

  const share = async () => {
    try {
      await navigator.share({ text: t("breb_key_share_text", { key: keyValue }) });
    } catch (error) {
      // Closing the share sheet is a choice; anything else falls back to copying.
      if ((error as { name?: unknown } | null)?.name !== "AbortError") {
        void copy(keyValue, t("breb_key_copied"));
      }
    }
  };

  return (
    <section
      aria-label={t("breb_key_label")}
      className={cn(
        "rounded-3xl p-4 text-white shadow-sm sm:p-5",
        pending || dimmed ? BREB_KEY_TONE.pending : BREB_KEY_TONE.live,
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <img src={logoWhiteUrl} alt="Coloca" className="h-4" />
        <div className="flex flex-wrap items-center justify-end gap-2">
          {pending && (
            <span className="inline-flex items-center gap-1 rounded-full bg-black/20 px-2.5 py-0.5 text-xs font-semibold">
              <Clock className="size-3" aria-hidden="true" />
              {t("breb_key_pending_badge")}
            </span>
          )}
          <span className="rounded-full bg-black/15 px-2.5 py-0.5 text-xs font-semibold tracking-wide">
            {formatRail("BREB")}
          </span>
        </div>
      </div>
      <p className="mt-5 text-xs font-medium text-white/90">{t("breb_key_label")}</p>
      <p className="mt-1 font-mono text-2xl font-semibold tracking-tight break-all select-all sm:text-3xl">
        {keyValue}
      </p>
      {holderName && (
        <p className="mt-2 text-xs text-white/90">{t("breb_key_holder_hint", { name: holderName })}</p>
      )}
      {showActions && (
        <div className={cn("mt-4 grid gap-2", canShare && "grid-cols-2")}>
          <ColocaButton
            size="sm"
            icon={copied ? Check : Copy}
            onClick={() => void copy(keyValue, t("breb_key_copied"))}
            className="text-foreground"
          >
            {copied ? t("breb_key_copied") : t("breb_key_copy")}
          </ColocaButton>
          {canShare && (
            <ColocaButton size="sm" icon={Share2} onClick={() => void share()} className="text-foreground">
              {t("breb_key_share")}
            </ColocaButton>
          )}
        </div>
      )}
    </section>
  );
}
